import { allFromSnapshot, firestoreFieldValueDelete, oneFromSnapshot, } from "@buildwithflux/firestore-compatibility-layer";
import { aggregateReviewStatus, aggregateReviewCheckStatus, createNewReviewColumnUid, createNewReviewUid, DocumentUid, reviewData, ReviewStatus, reviewUidToReviewTypeMap, } from "@buildwithflux/models";
import { mapValues } from "lodash";
/** @inheritDoc */
export class FirestoreReviewRepository {
    db;
    constructor(db) {
        this.db = db;
    }
    /** @inheritDoc */
    async getByUid(documentUid, reviewUid) {
        return oneFromSnapshot(await this.db.reviewCollection(documentUid).doc(reviewUid).get(), () => undefined);
    }
    /** @inheritDoc */
    async getAllByDocumentUid(documentUid) {
        const query = this.db.reviewCollection(documentUid).limit(500);
        return allFromSnapshot(await query.get());
    }
    async getAllGroupedByCategory(documentUid) {
        const reviews = await this.getAllByDocumentUid(documentUid);
        return this.groupByCategory(this.addMissingReviews(reviews, documentUid));
    }
    /** @inheritDoc */
    async updateAssignedStatus(documentUid, reviewUid, assignedStatus) {
        let review = await this.getByUid(documentUid, reviewUid);
        if (!review) {
            const type = reviewUidToReviewTypeMap[reviewUid];
            review = await this.createReview(type, documentUid);
        }
        await this.db.reviewCollection(documentUid).doc(reviewUid).update(`data.assignedStatus`, assignedStatus);
        return { ...review, assignedStatus };
    }
    /** @inheritDoc */
    async removeAssignedStatus(documentUid, reviewUid) {
        const review = await this.getByUid(documentUid, reviewUid);
        if (review) {
            await this.db
                .reviewCollection(documentUid)
                .doc(reviewUid)
                .update(`data.assignedStatus`, await firestoreFieldValueDelete(this.db));
            return { ...review, assignedStatus: undefined };
        }
    }
    /** @inheritDoc */
    async updateReviewCheckAssignedStatus(documentUid, reviewUid, reviewCheckUid, assignedStatus) {
        let review = await this.getByUid(documentUid, reviewUid);
        if (!review) {
            const type = reviewUidToReviewTypeMap[reviewUid];
            review = await this.createReview(type, documentUid, {
                [reviewCheckUid]: {
                    uid: reviewCheckUid,
                    reviewUid,
                    documentUid,
                    assignedStatus,
                    name: "",
                    description: "",
                    data: {},
                    index: 0,
                    status: ReviewStatus.OPEN,
                    createdAt: Date.now(),
                    updatedAt: Date.now(),
                },
            });
            return review;
        }
        if (!review.checks[reviewCheckUid]) {
            await this.db
                .reviewCollection(documentUid)
                .doc(reviewUid)
                .update(`data.checks`, {
                ...review.checks,
                [reviewCheckUid]: {
                    uid: reviewCheckUid,
                    reviewUid,
                    documentUid,
                    name: reviewCheckUid,
                    assignedStatus,
                },
            });
        }
        else {
            await this.db
                .reviewCollection(documentUid)
                .doc(reviewUid)
                .update(`data.checks.${reviewCheckUid}.assignedStatus`, assignedStatus);
        }
        return {
            ...review,
            checks: {
                ...review.checks,
                [reviewCheckUid]: { ...review.checks[reviewCheckUid], assignedStatus },
            },
        };
    }
    /** @inheritDoc */
    async removeReviewCheckAssignedStatus(documentUid, reviewUid, reviewCheckUid) {
        const review = await this.getByUid(documentUid, reviewUid);
        if (review) {
            await this.db
                .reviewCollection(documentUid)
                .doc(reviewUid)
                .update(`data.checks.${reviewCheckUid}.assignedStatus`, await firestoreFieldValueDelete(this.db));
            return { ...review, assignedStatus: undefined };
        }
        return review;
    }
    /** @inheritDoc */
    async saveReview(documentUid, review) {
        await this.db.reviewCollection(documentUid).doc(review.uid).set(review);
        return review;
    }
    /** @inheritDoc */
    async resetReview(documentUid, reviewUid) {
        return this.createReview(reviewUidToReviewTypeMap[reviewUid], documentUid, {});
    }
    subscribeToAllGroupedByCategory(documentUid, onChange, onError) {
        return this.db.reviewCollection(documentUid).onSnapshot(async (snap) => {
            const existingReviews = allFromSnapshot(snap);
            const fullReviews = this.addMissingReviews(existingReviews, documentUid);
            onChange(this.groupByCategory(fullReviews));
        }, onError);
    }
    subscribeToReview(documentUid, reviewUid, onChange, onError) {
        return this.db
            .reviewCollection(documentUid)
            .doc(reviewUid)
            .onSnapshot(async (snap) => {
            const review = oneFromSnapshot(snap, () => undefined);
            if (review) {
                onChange(review);
            }
        }, onError);
    }
    async createReview(type, documentUid, checks = {}) {
        const data = reviewData[type];
        const reviewUid = createNewReviewUid(type);
        const review = {
            ...data,
            uid: reviewUid,
            documentUid,
            status: ReviewStatus.OPEN,
            createdAt: Date.now(),
            updatedAt: Date.now(),
            checks,
            columns: mapValues(data.columns ?? {}, (column) => ({
                ...column,
                documentUid,
                reviewUid,
                uid: createNewReviewColumnUid(column.name),
            })),
        };
        await this.db.reviewCollection(DocumentUid.parse(review.documentUid)).doc(review.uid).set(review);
        return review;
    }
    groupByCategory(reviews) {
        const createCategory = (name) => ({ name, status: ReviewStatus.OPEN, reviews: [] });
        const categories = Object.values(reviews.reduce((a, review) => {
            const category = a[review.category] ?? createCategory(review.category);
            const reviewChecks = Object.values(review.checks).filter(Boolean);
            category.reviews.push({
                ...review,
                status: reviewChecks.length
                    ? aggregateReviewCheckStatus(reviewChecks.map((check) => check.status))
                    : review.status,
            });
            return { ...a, [review.category]: category };
        }, {}));
        return categories.map((category) => {
            const status = category.reviews
                .map((review) => (review.assignedStatus ? undefined : review.status))
                .filter(Boolean);
            return {
                ...category,
                status: status.length > 1 ? aggregateReviewStatus(status) : status[0] ?? ReviewStatus.OPEN,
            };
        });
    }
    /**
     * Add reviews that don't exist in the database yet (i.e. that haven't been run yet in this document).
     * This function will add default review data for the missing reviews.
     */
    addMissingReviews(reviews, documentUid) {
        return Object.values(reviewData).map((review) => {
            const reviewUid = createNewReviewUid(review.type);
            return (reviews.find((review) => review.uid === reviewUid) || {
                ...review,
                uid: reviewUid,
                status: ReviewStatus.UNRUN,
                documentUid,
                createdAt: 0,
                updatedAt: 0,
                checks: {},
                columns: mapValues(review.columns, (column) => ({
                    ...column,
                    uid: createNewReviewColumnUid(column.name),
                    reviewUid,
                    documentUid,
                })),
            });
        });
    }
}
//# sourceMappingURL=FirestoreReviewRepository.js.map